<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import { format, parseISO } from 'date-fns';
import AgentLeaveForm from './AgentLeaveForm.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import { listLeave, deleteLeave } from '../../../../api/realEstate/agentLeave';

// Layer 2 — agent leave (docs/real-estate/be/10-api-reference.md §A9). The
// add form itself now lives in AgentLeaveForm.vue (also used standalone by
// the Agent Calendar's drag-select chooser) — this modal keeps the list +
// delete, and the project picker used to resolve each row's project name.
// `property_id: null` (the "All projects" option) applies the leave at
// every project.
export default {
  name: 'AgentLeaveModal',
  components: { AgentLeaveForm, DeleteModal },
  props: {
    agent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      leaveItems: [],
      isLoadingLeave: false,
      isLoadingProjects: false,
      itemToDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters('realEstate', { projects: 'getProjectOptions' }),
  },
  mounted() {
    this.loadLeave();
    this.loadProjects();
  },
  methods: {
    ...mapActions('realEstate', ['fetchProjectOptions']),
    // Always refreshes (a project may have been added or renamed since the
    // list was cached); the spinner only shows while there's nothing yet.
    async loadProjects() {
      this.isLoadingProjects = !this.projects.length;
      try {
        await this.fetchProjectOptions();
      } finally {
        this.isLoadingProjects = false;
      }
    },
    async loadLeave() {
      this.isLoadingLeave = true;
      try {
        const { data } = await listLeave(this.agent.agent_id, {
          from_date: format(new Date(), 'yyyy-MM-dd'),
        });
        this.leaveItems = data.results
          .slice()
          .sort((a, b) => (a.unavailable_date < b.unavailable_date ? -1 : 1));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.AGENT_LEAVE.ERROR_LOAD')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoadingLeave = false;
      }
    },
    projectName(propertyId) {
      if (!propertyId) return this.$t('REAL_ESTATE.AGENT_LEAVE.ALL_PROJECTS');
      const project = this.projects.find(p => p.property_id === propertyId);
      return project ? project.name : propertyId;
    },
    formatDate(d) {
      return format(parseISO(d), 'd MMM yyyy');
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await deleteLeave(this.agent.agent_id, this.itemToDelete.id);
        useAlert(this.$t('REAL_ESTATE.AGENT_LEAVE.SUCCESS_DELETED'));
        this.itemToDelete = null;
        await this.loadLeave();
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.AGENT_LEAVE.ERROR_DELETE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.AGENT_LEAVE.MODAL_TITLE', { name: agent.name }) }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <DeleteModal
        v-if="itemToDelete"
        :delete-name="
          itemToDelete.reason || formatDate(itemToDelete.unavailable_date)
        "
        :is-loading="isDeleting"
        @confirm="confirmDelete"
        @close="itemToDelete = null"
      />

      <p v-if="isLoadingLeave" class="text-sm text-slate-400 mb-4">
        {{ $t('REAL_ESTATE.AGENT_LEAVE.LOADING') }}
      </p>
      <p v-else-if="!leaveItems.length" class="text-sm text-slate-400 mb-4">
        {{ $t('REAL_ESTATE.AGENT_LEAVE.EMPTY') }}
      </p>
      <ul v-else class="space-y-2 mb-4">
        <li
          v-for="item in leaveItems"
          :key="item.id"
          class="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200"
        >
          <span class="w-24 shrink-0 text-slate-900 dark:text-slate-100">
            {{ formatDate(item.unavailable_date) }}
          </span>
          <span class="w-28 shrink-0">
            {{
              item.all_day
                ? $t('REAL_ESTATE.AGENT_LEAVE.ALL_DAY_LABEL')
                : `${item.start_time}–${item.end_time}`
            }}
          </span>
          <span class="w-36 shrink-0 truncate">
            {{ projectName(item.property_id) }}
          </span>
          <span class="flex-1 text-slate-400 truncate">
            {{ item.reason }}
          </span>
          <woot-button
            variant="clear"
            size="small"
            color-scheme="alert"
            icon="delete"
            @click="itemToDelete = item"
          />
        </li>
      </ul>

      <div class="pt-4 border-t border-slate-200 dark:border-slate-700">
        <p class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
          {{ $t('REAL_ESTATE.AGENT_LEAVE.ADD_TITLE') }}
        </p>
        <AgentLeaveForm :agent="agent" @added="loadLeave" />
      </div>

      <div
        class="flex justify-end gap-3 pt-4 mt-4 border-t border-slate-200 dark:border-slate-700"
      >
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('REAL_ESTATE.AGENT_LEAVE.CLOSE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
